// Photo Watermark — browser-only watermarking engine.
//
// State model: a single `state` object holds files, layers (text+logo), layout
// (9-position presets / free / tile) and output options (format, quality,
// EXIF preserve, GPS strip, C2PA). Positions are stored as 0..1 normalized
// coordinates so a watermark configured on the preview scales correctly to
// the source image's native resolution at export time.
//
// EXIF (piexifjs) and C2PA (@contentauth/c2pa) are lazy-imported so the
// 50-500KB they cost only land when the user actually opts into them.
(function () {
    'use strict';

    const SLUG = 'photo-watermark';
    const $ = (id) => document.getElementById(id);
    const T = window.__PW_T || {};

    // Canonical system font stacks — shared by the default state, the font
    // registry and the fallback tails so a value always matches a picker option.
    const SYS_SANS = "system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
    const SYS_SERIF = "Georgia,'Times New Roman',serif";
    const SYS_MONO = "'Courier New',ui-monospace,monospace";

    // ───── Helpers ─────
    const safe = (fn) => { try { return fn(); } catch (e) { /* noop */ } };
    const ae = (event, payload) => safe(() => window.__ae && window.__ae(event, Object.assign({ slug: SLUG }, payload || {})));
    const aeCrit = (event, payload) => safe(() => window.__aeCritical && window.__aeCritical(event, Object.assign({ slug: SLUG }, payload || {})));
    const toast = (msg, type) => safe(() => window.__toast && window.__toast(msg, type || 'error'));
    const handleErr = (err, ctx) => safe(() => window.__handleError ? window.__handleError(err, ctx) : (console.error(ctx, err), toast(String(err && err.message || err), 'error')));
    const announce = (msg) => safe(() => window.__announce && window.__announce(msg));

    function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }
    function fmt(n, digits) { return Math.round(n * Math.pow(10, digits || 0)) / Math.pow(10, digits || 0); }
    function pct(v) { return Math.round(v * 100) + '%'; }

    // ───── State ─────
    const state = {
        files: [],          // [{file, image, exifBytes, naturalWidth, naturalHeight, mime}]
        activeIndex: 0,
        layout: {
            position: 'br',           // 'tl'..'br' | 'free'
            margin: 0.03,             // fraction of min(w,h)
            free: { x: 0.9, y: 0.9 }, // when position='free' or after drag
            tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 },
        },
        text: {
            value: '',
            font: SYS_SANS,
            size: 0.05,               // fraction of min(w,h)
            color: '#ffffff',
            opacity: 0.6,
            rotation: 0,
            letterSpacing: 0,
            bold: false,
            italic: false,
            stroke: { enabled: false, color: '#000000', width: 2 },
            shadow: { enabled: false, color: '#000000', blur: 4 },
        },
        logo: {
            image: null,
            name: '',
            width: 0.25,
            opacity: 1,
            rotation: 0,
        },
        output: {
            format: 'original',       // 'original' | 'jpeg' | 'png' | 'webp'
            size: 'original',         // 'original' | long-side px (number)
            quality: 0.92,
            preserveExif: true,
            stripGps: false,
            stripAll: false,
            c2pa: { enabled: false, author: '' },
        },
        compare: false,
        compareX: 0.5,
    };

    // ───── Font registry + preview picker ─────
    // Curated self-hosted set (OFL; latin+latin-ext+cyrillic woff2 declared as
    // @font-face in the blade). A native <select> can't render each option in
    // its own typeface across browsers, so we drive a hidden <select> (the state
    // source) from a custom listbox where every row previews the real font.
    // Every web family is declared font-weight:100 900 → ctx.font at any weight
    // resolves the face; document.fonts.load() must succeed or the canvas
    // silently rasterises a fallback typeface.
    const FALLBACK = {
        system: SYS_SANS,
        sans: "system-ui,'Segoe UI',Roboto,Arial,sans-serif",
        serif: SYS_SERIF,
        script: "'Segoe Script','Bradley Hand',cursive",
        display: "Impact,'Arial Black',system-ui,sans-serif",
    };
    // Web family → group fallback tail, so glyphs a Latin/Cyrillic display face
    // lacks (e.g. CJK / Arabic) render in a system font instead of tofu.
    const webStack = (fam, group) => "'" + fam + "'," + (FALLBACK[group] || FALLBACK.sans);
    const FONT_REG = [
        { id: 'sys-sans', group: 'system', web: false, name: T.font_sys_sans || 'System Sans', stack: SYS_SANS },
        { id: 'sys-serif', group: 'system', web: false, name: T.font_sys_serif || 'Serif', stack: SYS_SERIF },
        { id: 'sys-mono', group: 'system', web: false, name: T.font_sys_mono || 'Monospace', stack: SYS_MONO },
        // `ttf` = full-charset installable file in public/fonts/watermark/ttf/
        // (unmodified google/fonts builds; OFL permits handing the file over —
        // the Google Fonts "Download family" pattern).
        { id: 'montserrat', group: 'sans', web: true, name: 'Montserrat', fam: 'PW Montserrat', ttf: 'Montserrat-VariableFont_wght.ttf' },
        { id: 'oswald', group: 'sans', web: true, name: 'Oswald', fam: 'PW Oswald', ttf: 'Oswald-VariableFont_wght.ttf' },
        { id: 'comfortaa', group: 'sans', web: true, name: 'Comfortaa', fam: 'PW Comfortaa', ttf: 'Comfortaa-VariableFont_wght.ttf' },
        { id: 'playfair', group: 'serif', web: true, name: 'Playfair Display', fam: 'PW Playfair', ttf: 'PlayfairDisplay-VariableFont_wght.ttf' },
        { id: 'yeseva', group: 'serif', web: true, name: 'Yeseva One', fam: 'PW Yeseva', ttf: 'YesevaOne-Regular.ttf' },
        { id: 'russo', group: 'display', web: true, name: 'Russo One', fam: 'PW Russo', ttf: 'RussoOne-Regular.ttf' },
        // "Runic" (user request #1054): leads with the LOCAL "Runic MT Condensed"
        // (Monotype ships it with Office — the requester and many Windows users
        // get the true face; we cannot redistribute it), then falls back to the
        // bundled OFL MedievalSharp served as 'PW Runic'. Latin only — cyrillic
        // glyphs drop to the display fallback, visible live in the preview.
        // The download hands over the OFL lookalike under its REAL name
        // (dlName + dlAlt wording), never Monotype's file.
        { id: 'runic', group: 'display', web: true, name: 'Runic', fam: 'PW Runic', stack: "'Runic MT Condensed','PW Runic'," + FALLBACK.display, ttf: 'MedievalSharp-Regular.ttf', dlName: 'MedievalSharp', dlAlt: true },
        { id: 'caveat', group: 'script', web: true, name: 'Caveat', fam: 'PW Caveat', ttf: 'Caveat-VariableFont_wght.ttf' },
        { id: 'marck', group: 'script', web: true, name: 'Marck Script', fam: 'PW Marck', ttf: 'MarckScript-Regular.ttf' },
        { id: 'pacifico', group: 'script', web: true, name: 'Pacifico', fam: 'PW Pacifico', ttf: 'Pacifico-Regular.ttf' },
        { id: 'lobster', group: 'script', web: true, name: 'Lobster', fam: 'PW Lobster', ttf: 'Lobster-Regular.ttf' },
    ].map((f) => Object.assign(f, { stack: f.stack || webStack(f.fam, f.group) }));

    const GROUP_ORDER = ['system', 'sans', 'serif', 'display', 'script'];
    const GROUP_LABEL = {
        system: T.font_group_system || 'System', sans: T.font_group_sans || 'Sans-serif',
        serif: T.font_group_serif || 'Serif', display: T.font_group_display || 'Display',
        script: T.font_group_script || 'Handwriting',
    };

    const loadedFonts = new Set();
    const firstFamily = (stack) => (String(stack || '').split(',')[0] || '').replace(/['"]/g, '').trim();
    const stackFamilies = (stack) => String(stack || '').split(',').map((s) => s.trim().replace(/^['"]|['"]$/g, '')).filter(Boolean);
    const isWebFamily = (fam) => /^PW /.test(fam);
    const stackHasWebFace = (stack) => stackFamilies(stack).some(isWebFamily);
    const cleanFamilyName = (fam) => fam.replace(/^PW /, '');
    const fontOptById = (id) => FONT_REG.find((f) => f.id === id) || null;
    const fontOptByStack = (stack) => FONT_REG.find((f) => f.stack === stack) || null;
    const stackOf = (id) => { const o = fontOptById(id); return o ? o.stack : SYS_SANS; };

    // Resolve a stack's woff2 face(s) before the canvas draws with it. Loads both
    // 400 and 700 so the Bold toggle never falls back. Resolves fast for system
    // stacks (no @font-face to fetch) and never rejects — a failed load simply
    // leaves the CSS fallback in place.
    async function ensureFontLoaded(stack) {
        const key = String(stack || '');
        if (!key || loadedFonts.has(key)) return;
        // Load the lead family AND every self-hosted 'PW ' face in the tail.
        // The Runic stack leads with the user's LOCAL "Runic MT Condensed"
        // (resolves instantly, fetch-free) and falls back to our 'PW Runic'
        // woff2 — which must be fetched too, or users without the local font
        // get the group fallback baked into the canvas.
        const fams = stackFamilies(key).filter((f, i) => i === 0 || isWebFamily(f));
        try {
            if (document.fonts && document.fonts.load) {
                await Promise.all(fams.map((f) => Promise.all([
                    document.fonts.load('400 40px "' + f + '"'),
                    document.fonts.load('700 40px "' + f + '"'),
                ])));
            }
        } catch (e) { /* draw the fallback */ }
        loadedFonts.add(key);
    }

    let fontPopOpen = false, fontFocusIdx = -1, fontPreviewsLoaded = false;
    const fontRows = () => Array.prototype.slice.call(dom.pwFontPop.querySelectorAll('.pw-fontpick-opt'));

    // Reflect hidden <select>.value into the button (label + preview face) and
    // the selected row. Handles values not in the registry (old localStorage
    // presets) by showing the raw family name.
    function updateFontCurrent() {
        const cur = dom.pwFontCurrent;
        if (!cur || !dom.pwFont) return;
        const stack = dom.pwFont.value;
        const opt = fontOptByStack(stack);
        cur.textContent = opt ? opt.name : (cleanFamilyName(firstFamily(stack)) || (T.font_sys_sans || 'Font'));
        cur.style.fontFamily = stack || SYS_SANS;
        if (dom.pwFontPop) {
            dom.pwFontPop.querySelectorAll('.pw-fontpick-opt').forEach((el) => {
                const on = el.dataset.val === stack;
                el.classList.toggle('is-selected', on);
                el.setAttribute('aria-selected', on ? 'true' : 'false');
            });
        }
        // "Download font" link — web fonts only (their OFL license lets us hand
        // the installable file over, the Google Fonts "Download family" pattern;
        // system stacks have nothing to give). The Runic entry hands the OFL
        // lookalike under its REAL name via the alt wording — never Monotype's
        // file (which we cannot redistribute).
        if (dom.pwFontDownload) {
            if (opt && opt.web && opt.ttf) {
                const dlName = opt.dlName || opt.name;
                dom.pwFontDownload.href = '/fonts/watermark/ttf/' + opt.ttf;
                dom.pwFontDownload.setAttribute('download', opt.ttf);
                dom.pwFontDownloadLabel.textContent = (opt.dlAlt
                    ? (T.font_download_alt || 'Download lookalike font: :name (TTF, OFL license)')
                    : (T.font_download || 'Download font: :name (TTF, OFL license)')).replace(':name', dlName);
                dom.pwFontDownload.hidden = false;
            } else {
                dom.pwFontDownload.hidden = true;
            }
        }
    }

    function buildFontPicker() {
        const sel = dom.pwFont, pop = dom.pwFontPop, btn = dom.pwFontBtn;
        if (!sel || !pop || !btn) return;
        sel.innerHTML = '';
        pop.innerHTML = '';
        GROUP_ORDER.forEach((g) => {
            const items = FONT_REG.filter((f) => f.group === g);
            if (!items.length) return;
            const head = document.createElement('div');
            head.className = 'pw-fontpick-group';
            head.textContent = GROUP_LABEL[g] || g;
            pop.appendChild(head);
            items.forEach((f) => {
                const o = document.createElement('option');
                o.value = f.stack; o.textContent = f.name;
                sel.appendChild(o);

                const row = document.createElement('div');
                row.className = 'pw-fontpick-opt';
                row.setAttribute('role', 'option');
                row.tabIndex = -1;
                row.id = 'pw-font-opt-' + f.id;
                row.dataset.val = f.stack;
                const nm = document.createElement('span');
                nm.className = 'pw-fontpick-optname';
                nm.textContent = f.name;
                nm.style.fontFamily = f.stack; // preview in the actual typeface
                const chk = document.createElement('span');
                chk.className = 'pw-fontpick-check';
                chk.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
                row.appendChild(nm); row.appendChild(chk);
                row.addEventListener('click', () => selectFont(f.stack));
                pop.appendChild(row);
            });
        });
        updateFontCurrent();

        btn.addEventListener('click', toggleFontPop);
        btn.addEventListener('keydown', onFontBtnKey);
        pop.addEventListener('keydown', onFontPopKey);
        document.addEventListener('click', (e) => {
            if (fontPopOpen && dom.pwFontPick && !dom.pwFontPick.contains(e.target)) closeFontPop();
        });
        if (dom.pwFontDownload) {
            dom.pwFontDownload.addEventListener('click', () => {
                const opt = fontOptByStack(sel.value);
                ae('font_downloaded', { font: (opt && (opt.dlName || opt.name)) || '' });
            });
        }
    }

    function openFontPop() {
        if (fontPopOpen) return;
        fontPopOpen = true;
        dom.pwFontPop.hidden = false;
        dom.pwFontBtn.setAttribute('aria-expanded', 'true');
        // Lazily fetch every preview face the first time the list opens.
        if (!fontPreviewsLoaded) {
            fontPreviewsLoaded = true;
            FONT_REG.filter((f) => f.web).forEach((f) => ensureFontLoaded(f.stack));
        }
        const rows = fontRows();
        const sel = rows.findIndex((r) => r.dataset.val === dom.pwFont.value);
        focusFontRow(sel < 0 ? 0 : sel);
    }
    function closeFontPop() {
        if (!fontPopOpen) return;
        fontPopOpen = false;
        dom.pwFontPop.hidden = true;
        dom.pwFontBtn.setAttribute('aria-expanded', 'false');
        fontRows().forEach((r) => r.classList.remove('is-focus'));
    }
    function toggleFontPop() { fontPopOpen ? closeFontPop() : openFontPop(); }

    function focusFontRow(idx) {
        const rows = fontRows();
        if (!rows.length) return;
        fontFocusIdx = Math.max(0, Math.min(rows.length - 1, idx));
        rows.forEach((r) => r.classList.remove('is-focus'));
        const row = rows[fontFocusIdx];
        row.classList.add('is-focus');
        try { row.focus({ preventScroll: true }); } catch (e) { row.focus(); }
        row.scrollIntoView({ block: 'nearest' }); // nearest only — never jumps the page
    }

    function selectFont(stack) {
        if (dom.pwFont.value !== stack) {
            dom.pwFont.value = stack;
            dom.pwFont.dispatchEvent(new Event('change'));
        }
        updateFontCurrent();
        closeFontPop();
        dom.pwFontBtn.focus();
    }

    function onFontBtnKey(e) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFontPop(); }
    }
    function onFontPopKey(e) {
        if (!fontPopOpen) return;
        const rows = fontRows();
        switch (e.key) {
            case 'ArrowDown': e.preventDefault(); focusFontRow(fontFocusIdx + 1); break;
            case 'ArrowUp': e.preventDefault(); focusFontRow(fontFocusIdx - 1); break;
            case 'Home': e.preventDefault(); focusFontRow(0); break;
            case 'End': e.preventDefault(); focusFontRow(rows.length - 1); break;
            case 'Enter': case ' ': {
                e.preventDefault();
                const row = rows[fontFocusIdx];
                if (row) selectFont(row.dataset.val);
                break;
            }
            case 'Escape': e.preventDefault(); closeFontPop(); dom.pwFontBtn.focus(); break;
            case 'Tab': closeFontPop(); break;
        }
    }

    // ───── DOM refs (gathered once on init) ─────
    const dom = {};
    function refs() {
        const ids = [
            'pwApp', 'pwUploadScreen', 'pwUpload', 'pwWorkspace',
            'pwStage', 'pwCanvas', 'pwCompareHandle', 'pwSizePill',
            'pwCompareBtn', 'pwContrastHint',
            'pwBatch', 'pwBatchList', 'pwBatchAdd', 'pwBatchWarning',
            'pwText', 'pwFont', 'pwFontPick', 'pwFontBtn', 'pwFontCurrent', 'pwFontPop',
            'pwFontDownload', 'pwFontDownloadLabel', 'pwSize', 'pwSizeVal',
            'pwColor', 'pwColorHex', 'pwOpacity', 'pwOpacityVal',
            'pwRotation', 'pwRotationVal', 'pwLetterSpacing', 'pwLetterSpacingVal',
            'pwBold', 'pwItalic',
            'pwStrokeEnabled', 'pwStrokeRow', 'pwStrokeColor', 'pwStrokeWidth', 'pwStrokeWidthVal',
            'pwShadowEnabled', 'pwShadowRow', 'pwShadowColor', 'pwShadowBlur', 'pwShadowBlurVal',
            'pwLogoPreview', 'pwLogoThumb', 'pwLogoName', 'pwLogoRemove',
            'pwLogoPickLabel', 'pwLogoPickText', 'pwLogoInput',
            'pwLogoWidth', 'pwLogoWidthVal', 'pwLogoOpacity', 'pwLogoOpacityVal',
            'pwLogoRotation', 'pwLogoRotationVal',
            'pwMargin', 'pwMarginVal',
            'pwTile', 'pwTileRow', 'pwTileAngle', 'pwTileAngleVal',
            'pwTileStepX', 'pwTileStepXVal', 'pwTileStepY', 'pwTileStepYVal',
            'pwFormat', 'pwExportSize', 'pwQuality', 'pwQualityField', 'pwQualityVal',
            'pwPreserveExif', 'pwStripGps', 'pwStripAll',
            'pwC2pa', 'pwC2paAuthorField', 'pwC2paAuthor',
            'pwPresetList', 'pwPresetSave',
            'pwApplyBtn', 'pwApplyLabel', 'pwCancelBtn',
            'pwProgress', 'pwProgressFill', 'pwProgressText',
        ];
        ids.forEach((id) => dom[id] = $(id));
    }

    // ───── Render scheduling ─────
    let renderQueued = false;
    function requestRender() {
        if (renderQueued) return;
        renderQueued = true;
        requestAnimationFrame(() => { renderQueued = false; render(); });
    }

    // ───── Loading images ─────
    // We keep the blob URL alive for as long as the file lives in state.files
    // (batch thumbnails reuse `image.src`). The URL is revoked in removeFile().
    function loadImage(blob) {
        // iPhone HEIC/HEIF: decode to JPEG via heic-to first so <img> can load it.
        const prep = window.HeicDecode ? window.HeicDecode.normalize(blob, window.__ae || null) : Promise.resolve(blob);
        return prep.then((b) => new Promise((resolve, reject) => {
            const url = URL.createObjectURL(b);
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image_decode')); };
            img.src = url;
        }));
    }

    function disposeImage(img) {
        try {
            if (img && img.src && img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
        } catch (e) { /* noop */ }
    }

    async function readJpegExif(file) {
        // Cheap probe: only JPEG bytes have an APP1 marker that piexif reads.
        // We capture the original bytes once so the lazy-loaded EXIF module
        // can re-inject them later without us having to re-read the File.
        if (file.type !== 'image/jpeg' && !/\.jpe?g$/i.test(file.name)) return null;
        try {
            const ab = await file.arrayBuffer();
            return new Uint8Array(ab);
        } catch (e) {
            return null;
        }
    }

    async function addFiles(fileList) {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        const accepted = [];
        for (const file of files) {
            if (!/^image\//.test(file.type) && !/\.(jpg|jpeg|png|webp|gif|bmp|heic|heif)$/i.test(file.name)) {
                continue;
            }
            try {
                const image = await loadImage(file);
                const exifBytes = state.output.preserveExif ? await readJpegExif(file) : null;
                accepted.push({
                    file,
                    image,
                    exifBytes,
                    naturalWidth: image.naturalWidth,
                    naturalHeight: image.naturalHeight,
                    mime: file.type || guessMime(file.name),
                });
            } catch (e) {
                console.warn('[pw] could not load', file.name, e);
                toast((T.err_load || 'Could not load this image.') + ' — ' + file.name, 'error');
            }
        }

        if (!accepted.length) return;

        state.files = state.files.concat(accepted);
        state.activeIndex = state.files.length - accepted.length;

        ae('file_select', { count: accepted.length, total_after: state.files.length, total_mb: accepted.reduce((s, f) => s + f.file.size, 0) / 1048576 });

        showWorkspace();
        renderBatch();
        requestRender();
    }

    function guessMime(name) {
        const m = /\.([a-z0-9]+)$/i.exec(name || '');
        const ext = m ? m[1].toLowerCase() : '';
        return ({
            jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
            webp: 'image/webp', gif: 'image/gif', bmp: 'image/bmp',
            heic: 'image/heic', heif: 'image/heif',
        })[ext] || 'image/jpeg';
    }

    function showWorkspace() {
        if (!dom.pwUploadScreen.classList.contains('is-hidden')) {
            dom.pwUploadScreen.classList.add('is-hidden');
            dom.pwWorkspace.classList.add('is-active');
        }
    }

    function renderBatch() {
        const list = dom.pwBatchList;
        list.innerHTML = '';
        state.files.forEach((f, i) => {
            const row = document.createElement('div');
            row.className = 'pw-batch-item' + (i === state.activeIndex ? ' is-active' : '');
            const thumb = document.createElement('img');
            thumb.className = 'pw-batch-thumb';
            thumb.src = f.image.src;
            thumb.alt = '';
            const name = document.createElement('span');
            name.className = 'pw-batch-name';
            name.textContent = f.file.name;
            name.title = f.file.name;
            name.addEventListener('click', () => { state.activeIndex = i; renderBatch(); requestRender(); });
            const rm = document.createElement('button');
            rm.type = 'button';
            rm.className = 'pw-batch-remove';
            rm.title = T.batch_remove || 'Remove';
            rm.setAttribute('aria-label', T.batch_remove || 'Remove');
            rm.textContent = '×';
            rm.addEventListener('click', (e) => { e.stopPropagation(); removeFile(i); });
            row.appendChild(thumb);
            row.appendChild(name);
            row.appendChild(rm);
            list.appendChild(row);
        });

        if (state.files.length > 50 && T.batch_too_many) {
            dom.pwBatchWarning.classList.add('is-visible');
            dom.pwBatchWarning.textContent = T.batch_too_many.replace('{count}', state.files.length);
        } else {
            dom.pwBatchWarning.classList.remove('is-visible');
        }
    }

    function removeFile(index) {
        const removed = state.files[index];
        if (removed && removed.image) disposeImage(removed.image);
        state.files.splice(index, 1);
        if (state.files.length === 0) {
            dom.pwUploadScreen.classList.remove('is-hidden');
            dom.pwWorkspace.classList.remove('is-active');
            state.activeIndex = 0;
            return;
        }
        if (state.activeIndex >= state.files.length) state.activeIndex = state.files.length - 1;
        renderBatch();
        requestRender();
    }

    // ───── Logo ─────
    async function loadLogo(file) {
        if (!file) return;
        if (!/^image\//.test(file.type) && !/\.(png|svg|webp|jpe?g)$/i.test(file.name)) {
            toast(T.err_logo_load || 'Could not load the logo.', 'error');
            return;
        }
        try {
            const image = await loadImage(file);
            state.logo.image = image;
            state.logo.name = file.name;
            dom.pwLogoThumb.src = image.src;
            dom.pwLogoName.textContent = file.name;
            dom.pwLogoPreview.classList.add('is-visible');
            dom.pwLogoPickLabel.style.display = 'none';
            ae('logo_added', { mime: file.type });
            requestRender();
        } catch (e) {
            handleErr(e, 'logo_load');
        }
    }

    function clearLogo() {
        state.logo.image = null;
        state.logo.name = '';
        dom.pwLogoPreview.classList.remove('is-visible');
        dom.pwLogoPickLabel.style.display = '';
        dom.pwLogoInput.value = '';
        requestRender();
    }

    // ───── Position presets → normalized ─────
    const POS_MAP = {
        tl: [0.0, 0.0, 'left', 'top'],
        tc: [0.5, 0.0, 'center', 'top'],
        tr: [1.0, 0.0, 'right', 'top'],
        ml: [0.0, 0.5, 'left', 'middle'],
        mc: [0.5, 0.5, 'center', 'middle'],
        mr: [1.0, 0.5, 'right', 'middle'],
        bl: [0.0, 1.0, 'left', 'bottom'],
        bc: [0.5, 1.0, 'center', 'bottom'],
        br: [1.0, 1.0, 'right', 'bottom'],
    };

    function anchorFor(pos) {
        const p = POS_MAP[pos];
        return p ? { ax: p[2], ay: p[3] } : { ax: 'center', ay: 'middle' };
    }

    function anchorPoint(width, height, pos) {
        // Returns canvas-relative {x, y} for the anchor of a 9-position preset,
        // including margin inset from the edge.
        const margin = state.layout.margin * Math.min(width, height);
        const [nx, ny] = POS_MAP[pos] || POS_MAP.br;
        return {
            x: clamp(nx * width + (nx === 0 ? margin : nx === 1 ? -margin : 0), 0, width),
            y: clamp(ny * height + (ny === 0 ? margin : ny === 1 ? -margin : 0), 0, height),
            anchor: anchorFor(pos),
        };
    }

    // ───── Draw scene ─────
    // Scales the source image into a target Canvas, then layers text + logo.
    // The same routine is used for preview (display-size canvas) and for export
    // (native-size canvas) — only `targetW`/`targetH` differ.
    function drawScene(ctx, srcImage, targetW, targetH) {
        ctx.save();
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, targetW, targetH);
        ctx.drawImage(srcImage, 0, 0, targetW, targetH);
        ctx.restore();

        if (state.compare) return; // showing original only

        if (state.layout.tile.enabled) {
            drawTile(ctx, targetW, targetH);
        } else {
            drawSingleWatermark(ctx, targetW, targetH);
        }
    }

    function drawSingleWatermark(ctx, w, h) {
        const pos = state.layout.position;
        let cx, cy, anchor;
        if (pos === 'free') {
            cx = state.layout.free.x * w;
            cy = state.layout.free.y * h;
            anchor = { ax: 'center', ay: 'middle' };
        } else {
            const a = anchorPoint(w, h, pos);
            cx = a.x; cy = a.y; anchor = a.anchor;
        }
        ctx.save();
        ctx.translate(cx, cy);
        const angle = (state.text.rotation || 0) * Math.PI / 180;
        if (state.text.value || state.logo.image) ctx.rotate(angle);
        drawLayers(ctx, w, h, anchor);
        ctx.restore();
    }

    function drawTile(ctx, w, h) {
        const angle = state.layout.tile.angle * Math.PI / 180;
        const stepX = Math.max(state.layout.tile.stepX * w, 50);
        const stepY = Math.max(state.layout.tile.stepY * h, 50);
        // Diagonal coverage: rotate around the canvas center, draw a grid that
        // extends past the visible bounds enough to cover all corners after rotation.
        const cx = w / 2, cy = h / 2;
        const diag = Math.sqrt(w * w + h * h);
        const minX = -diag, maxX = diag, minY = -diag, maxY = diag;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(angle);
        const anchor = { ax: 'center', ay: 'middle' };
        for (let y = minY; y <= maxY; y += stepY) {
            for (let x = minX; x <= maxX; x += stepX) {
                ctx.save();
                ctx.translate(x, y);
                drawLayers(ctx, w, h, anchor);
                ctx.restore();
            }
        }
        ctx.restore();
    }

    function drawLayers(ctx, w, h, anchor) {
        // Layers are pre-rotated/translated by the caller; here we draw text
        // and logo around (0,0) according to the anchor.
        if (state.logo.image) drawLogo(ctx, w, h, anchor);
        if (state.text.value) drawText(ctx, w, h, anchor);
    }

    function drawText(ctx, w, h, anchor) {
        const t = state.text;
        const fontSize = Math.max(8, t.size * Math.min(w, h));
        const weight = t.bold ? '700' : '400';
        const style = t.italic ? 'italic' : 'normal';
        ctx.save();
        ctx.font = `${style} ${weight} ${fontSize}px ${t.font}`;
        ctx.textAlign = anchor.ax;
        ctx.textBaseline = anchor.ay;
        ctx.globalAlpha = t.opacity;
        if (t.letterSpacing) {
            // Canvas letterSpacing prop is supported in Chrome 99+/Safari 16+/FF; harmless on older browsers.
            ctx.letterSpacing = (t.letterSpacing * fontSize / 20) + 'px';
        }
        if (t.shadow.enabled) {
            ctx.shadowColor = t.shadow.color;
            ctx.shadowBlur = t.shadow.blur;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
        }
        if (t.stroke.enabled) {
            ctx.strokeStyle = t.stroke.color;
            ctx.lineWidth = t.stroke.width;
            ctx.lineJoin = 'round';
            ctx.strokeText(t.value, 0, 0);
        }
        ctx.fillStyle = t.color;
        ctx.fillText(t.value, 0, 0);
        ctx.restore();
    }

    function drawLogo(ctx, w, h, anchor) {
        const l = state.logo;
        if (!l.image) return;
        const targetW = l.width * w;
        const aspect = l.image.naturalHeight / l.image.naturalWidth;
        const targetH = targetW * aspect;
        let dx = 0, dy = 0;
        if (anchor.ax === 'left') dx = 0;
        else if (anchor.ax === 'right') dx = -targetW;
        else dx = -targetW / 2;
        if (anchor.ay === 'top') dy = 0;
        else if (anchor.ay === 'bottom') dy = -targetH;
        else dy = -targetH / 2;
        ctx.save();
        ctx.globalAlpha = l.opacity;
        if (l.rotation) {
            // Rotate around the anchor point so dragging stays intuitive.
            ctx.translate(0, 0);
            ctx.rotate(l.rotation * Math.PI / 180);
        }
        ctx.drawImage(l.image, dx, dy, targetW, targetH);
        ctx.restore();
    }

    // ───── Render preview ─────
    let previewSize = { w: 0, h: 0, scale: 1 };

    function render() {
        const active = state.files[state.activeIndex];
        if (!active) return;
        const cv = dom.pwCanvas;
        const ctx = cv.getContext('2d');

        const stage = dom.pwStage;
        const maxW = stage.clientWidth || 600;
        const aspect = active.naturalHeight / active.naturalWidth;
        const cssW = Math.min(maxW, active.naturalWidth);
        const cssH = Math.round(cssW * aspect);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        cv.style.width = cssW + 'px';
        cv.style.height = cssH + 'px';
        cv.width = Math.round(cssW * dpr);
        cv.height = Math.round(cssH * dpr);
        previewSize = { w: cv.width, h: cv.height, scale: dpr, cssW, cssH };

        // Fetch a web font before rasterising with it; draw the fallback now and
        // re-render crisp once the face lands (canvas equivalent of font-display:
        // swap). loadedFonts guards against a re-render loop.
        if (state.text.value) {
            const stack = state.text.font;
            if (stackHasWebFace(stack) && !loadedFonts.has(stack)) {
                ensureFontLoaded(stack).then(() => requestRender());
            }
        }

        drawScene(ctx, active.image, cv.width, cv.height);
        // Source dims, plus the DELIVERED dims whenever the export-size choice
        // (or the plan ceiling) will shrink them — the preview-side half of the
        // ceiling disclosure that the Output tab makes at the point of choice.
        const outDims = exportDimsFor(active);
        dom.pwSizePill.textContent = `${active.naturalWidth} × ${active.naturalHeight}`
            + (outDims.w < active.naturalWidth ? ` → ${outDims.w} × ${outDims.h}` : '');
        stage.classList.remove('is-loading');

        evaluateContrastHint();
        if (!firstPreviewFired) {
            firstPreviewFired = true;
            ae('preview_rendered', { width: active.naturalWidth, height: active.naturalHeight });
        }
    }

    let firstPreviewFired = false;

    // ───── Contrast hint ─────
    // Sample the pixels under the watermark anchor and warn when colors collide
    // (e.g. white text on a bright sky, black text on a dark background).
    function evaluateContrastHint() {
        const active = state.files[state.activeIndex];
        if (!active) { dom.pwContrastHint.classList.remove('is-visible'); return; }
        if (state.layout.tile.enabled) { dom.pwContrastHint.classList.remove('is-visible'); return; }
        if (!state.text.value && !state.logo.image) { dom.pwContrastHint.classList.remove('is-visible'); return; }
        const cv = dom.pwCanvas;
        const ctx = cv.getContext('2d');
        let cx, cy;
        if (state.layout.position === 'free') {
            cx = state.layout.free.x * cv.width;
            cy = state.layout.free.y * cv.height;
        } else {
            const a = anchorPoint(cv.width, cv.height, state.layout.position);
            cx = a.x; cy = a.y;
        }
        const sample = Math.max(20, Math.min(80, Math.round(state.text.size * Math.min(cv.width, cv.height) * 2)));
        const x0 = clamp(cx - sample / 2, 0, cv.width - sample);
        const y0 = clamp(cy - sample / 2, 0, cv.height - sample);
        try {
            const data = ctx.getImageData(Math.round(x0), Math.round(y0), Math.round(sample), Math.round(sample)).data;
            // Note: we read the canvas after the watermark was drawn; reading underneath
            // is hard once layered. As a pragmatic shortcut we sample anyway — the watermark
            // pixels are themselves near the chosen text color, so they don't fool the
            // dark/bright detection in the surrounding region most of the time.
            let lumSum = 0, count = 0;
            for (let i = 0; i < data.length; i += 16) { // every 4th pixel
                const r = data[i], g = data[i + 1], b = data[i + 2];
                lumSum += 0.299 * r + 0.587 * g + 0.114 * b;
                count++;
            }
            const meanLum = lumSum / count;
            const textLum = colorLum(state.text.color || '#ffffff');
            const hintEl = dom.pwContrastHint;
            const hintSpan = hintEl.querySelector('span');
            const wasVisible = hintEl.classList.contains('is-visible');
            // Track the direction so the auto-contrast "fix" button knows what
            // to suggest (white watermark for dark hint, black for bright hint).
            if (textLum > 180 && meanLum > 180) {
                hintEl.classList.add('is-visible');
                hintEl.dataset.direction = 'dark';
                hintSpan.textContent = T.layout_contrast_hint_dark || '';
            } else if (textLum < 70 && meanLum < 70) {
                hintEl.classList.add('is-visible');
                hintEl.dataset.direction = 'bright';
                hintSpan.textContent = T.layout_contrast_hint || '';
            } else {
                hintEl.classList.remove('is-visible');
            }
            const nowVisible = hintEl.classList.contains('is-visible');
            // Telemetry on the transition only — avoid firing 60×/sec while dragging.
            if (nowVisible && !wasVisible) ae('contrast_hint_shown', { direction: hintEl.dataset.direction });
        } catch (e) {
            // Reading pixels of an image from another origin would taint — but
            // all our images are local Blobs, so this should never trip.
            dom.pwContrastHint.classList.remove('is-visible');
        }
    }

    function colorLum(hex) {
        if (!hex || hex[0] !== '#') return 128;
        const s = hex.slice(1);
        let r, g, b;
        if (s.length === 3) {
            r = parseInt(s[0] + s[0], 16); g = parseInt(s[1] + s[1], 16); b = parseInt(s[2] + s[2], 16);
        } else {
            r = parseInt(s.substr(0, 2), 16); g = parseInt(s.substr(2, 2), 16); b = parseInt(s.substr(4, 2), 16);
        }
        return 0.299 * r + 0.587 * g + 0.114 * b;
    }

    // ───── Drag on preview ─────
    let dragging = false;
    function bindDrag() {
        const cv = dom.pwCanvas;
        const onDown = (e) => {
            if (state.layout.tile.enabled) return;
            const p = pointerPos(cv, e);
            // Switch to free positioning the first time the user drags
            state.layout.position = 'free';
            state.layout.free = { x: clamp(p.x / cv.width, 0, 1), y: clamp(p.y / cv.height, 0, 1) };
            updatePositionButtons();
            dragging = true;
            cv.classList.add('is-dragging');
            try { cv.setPointerCapture && cv.setPointerCapture(e.pointerId); } catch (_) {}
            ae('drag_start', {});
            requestRender();
            e.preventDefault();
        };
        const onMove = (e) => {
            if (!dragging) return;
            const p = pointerPos(cv, e);
            state.layout.free = { x: clamp(p.x / cv.width, 0, 1), y: clamp(p.y / cv.height, 0, 1) };
            requestRender();
        };
        const onUp = (e) => {
            if (!dragging) return;
            dragging = false;
            cv.classList.remove('is-dragging');
            try { cv.releasePointerCapture && cv.releasePointerCapture(e.pointerId); } catch (_) {}
            ae('drag_end', { x: fmt(state.layout.free.x, 2), y: fmt(state.layout.free.y, 2) });
        };
        cv.addEventListener('pointerdown', onDown);
        cv.addEventListener('pointermove', onMove);
        cv.addEventListener('pointerup', onUp);
        cv.addEventListener('pointercancel', onUp);
    }

    function pointerPos(cv, e) {
        const rect = cv.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (cv.width / rect.width);
        const y = (e.clientY - rect.top) * (cv.height / rect.height);
        return { x, y };
    }

    // ───── Bind controls ─────
    function bindControls() {
        // Tabs
        document.querySelectorAll('.pw-tab').forEach((btn) => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.pw-tab').forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
                document.querySelectorAll('.pw-tabpanel').forEach((p) => p.classList.remove('is-active'));
                btn.classList.add('is-active');
                btn.setAttribute('aria-selected', 'true');
                const panel = document.querySelector(`[data-panel="${btn.dataset.tab}"]`);
                if (panel) panel.classList.add('is-active');
            });
        });

        // Text
        dom.pwText.addEventListener('input', () => { state.text.value = dom.pwText.value; requestRender(); });
        dom.pwFont.addEventListener('change', () => {
            state.text.font = dom.pwFont.value;
            updateFontCurrent();
            // First word of the stack gives us the readable family name for analytics.
            const fam = cleanFamilyName(firstFamily(dom.pwFont.value));
            ae('font_changed', { family: fam.slice(0, 40) });
            // Fetch the face before re-rendering so the preview swaps to the real
            // typeface in one paint instead of flashing the fallback.
            ensureFontLoaded(state.text.font).then(() => requestRender());
        });

        bindRange(dom.pwSize, dom.pwSizeVal, (v) => { state.text.size = v / 100; return v + '%'; });
        bindRange(dom.pwOpacity, dom.pwOpacityVal, (v) => { state.text.opacity = v / 100; return v + '%'; });
        bindRange(dom.pwRotation, dom.pwRotationVal, (v) => { state.text.rotation = v; return v + '°'; });
        bindRange(dom.pwLetterSpacing, dom.pwLetterSpacingVal, (v) => { state.text.letterSpacing = v; return v; });
        bindRange(dom.pwMargin, dom.pwMarginVal, (v) => { state.layout.margin = v / 100; return v + '%'; });

        // Color sync
        const onColor = (v) => {
            const hex = sanitizeHex(v);
            if (!hex) return;
            state.text.color = hex;
            dom.pwColor.value = hex;
            dom.pwColorHex.value = hex;
            requestRender();
        };
        dom.pwColor.addEventListener('input', () => onColor(dom.pwColor.value));
        dom.pwColorHex.addEventListener('input', () => onColor(dom.pwColorHex.value));

        // Bold / italic
        dom.pwBold.addEventListener('click', () => {
            state.text.bold = !state.text.bold;
            dom.pwBold.classList.toggle('is-active', state.text.bold);
            dom.pwBold.setAttribute('aria-pressed', state.text.bold);
            requestRender();
        });
        dom.pwItalic.addEventListener('click', () => {
            state.text.italic = !state.text.italic;
            dom.pwItalic.classList.toggle('is-active', state.text.italic);
            dom.pwItalic.setAttribute('aria-pressed', state.text.italic);
            requestRender();
        });

        // Stroke
        dom.pwStrokeEnabled.addEventListener('change', () => {
            state.text.stroke.enabled = dom.pwStrokeEnabled.checked;
            dom.pwStrokeRow.hidden = !state.text.stroke.enabled;
            requestRender();
        });
        dom.pwStrokeColor.addEventListener('input', () => { state.text.stroke.color = dom.pwStrokeColor.value; requestRender(); });
        bindRange(dom.pwStrokeWidth, dom.pwStrokeWidthVal, (v) => { state.text.stroke.width = v; return v; });

        // Shadow
        dom.pwShadowEnabled.addEventListener('change', () => {
            state.text.shadow.enabled = dom.pwShadowEnabled.checked;
            dom.pwShadowRow.hidden = !state.text.shadow.enabled;
            requestRender();
        });
        dom.pwShadowColor.addEventListener('input', () => { state.text.shadow.color = dom.pwShadowColor.value; requestRender(); });
        bindRange(dom.pwShadowBlur, dom.pwShadowBlurVal, (v) => { state.text.shadow.blur = v; return v; });

        // Logo
        dom.pwLogoInput.addEventListener('change', (e) => {
            const f = e.target.files && e.target.files[0];
            if (f) loadLogo(f);
        });
        dom.pwLogoRemove.addEventListener('click', clearLogo);
        bindRange(dom.pwLogoWidth, dom.pwLogoWidthVal, (v) => { state.logo.width = v / 100; return v + '%'; });
        bindRange(dom.pwLogoOpacity, dom.pwLogoOpacityVal, (v) => { state.logo.opacity = v / 100; return v + '%'; });
        bindRange(dom.pwLogoRotation, dom.pwLogoRotationVal, (v) => { state.logo.rotation = v; return v + '°'; });

        // Position grid
        document.querySelectorAll('.pw-pos-cell').forEach((cell) => {
            cell.addEventListener('click', () => {
                state.layout.position = cell.dataset.pos;
                ae('position_changed', { to: cell.dataset.pos });
                updatePositionButtons();
                requestRender();
            });
        });

        // Tile
        dom.pwTile.addEventListener('change', () => {
            state.layout.tile.enabled = dom.pwTile.checked;
            dom.pwTileRow.hidden = !state.layout.tile.enabled;
            ae('tile_toggled', { enabled: dom.pwTile.checked ? 1 : 0 });
            requestRender();
        });
        bindRange(dom.pwTileAngle, dom.pwTileAngleVal, (v) => { state.layout.tile.angle = v; return v + '°'; });
        bindRange(dom.pwTileStepX, dom.pwTileStepXVal, (v) => { state.layout.tile.stepX = v / 100; return v + '%'; });
        bindRange(dom.pwTileStepY, dom.pwTileStepYVal, (v) => { state.layout.tile.stepY = v / 100; return v + '%'; });

        // Output
        dom.pwFormat.addEventListener('change', () => {
            state.output.format = dom.pwFormat.value;
            // Quality only matters for JPEG/WebP (lossy)
            const showQuality = ['jpeg', 'webp', 'original'].indexOf(state.output.format) !== -1;
            dom.pwQualityField.hidden = !showQuality;
            ae('format_changed', { to: state.output.format });
        });
        dom.pwExportSize.addEventListener('change', () => {
            const v = dom.pwExportSize.value;
            state.output.size = v === 'original' ? 'original' : (parseInt(v, 10) || 'original');
            ae('size_changed', { to: String(state.output.size) });
            requestRender(); // refresh the «→ W × H» disclosure in the size pill
        });
        bindRange(dom.pwQuality, dom.pwQualityVal, (v) => { state.output.quality = v / 100; return v; });
        dom.pwPreserveExif.addEventListener('change', () => { state.output.preserveExif = dom.pwPreserveExif.checked; });
        dom.pwStripGps.addEventListener('change', () => {
            state.output.stripGps = dom.pwStripGps.checked;
            ae('strip_gps_toggled', { enabled: dom.pwStripGps.checked ? 1 : 0 });
        });
        dom.pwStripAll.addEventListener('change', () => {
            state.output.stripAll = dom.pwStripAll.checked;
            if (state.output.stripAll) {
                state.output.preserveExif = false;
                dom.pwPreserveExif.checked = false;
            }
        });

        // C2PA — `c2pa_attempted` fires regardless of vendor availability so we
        // can size demand for the feature before paying for a CAI-listed cert.
        dom.pwC2pa.addEventListener('change', () => {
            state.output.c2pa.enabled = dom.pwC2pa.checked;
            dom.pwC2paAuthorField.hidden = !state.output.c2pa.enabled;
            if (state.output.c2pa.enabled) {
                preloadC2pa();
                ae('c2pa_attempted', { locale: (window.__LOCALE || 'en'), vendor_ready: window.__C2PA_VENDOR_READY ? 1 : 0 });
            }
        });
        dom.pwC2paAuthor.addEventListener('input', () => { state.output.c2pa.author = dom.pwC2paAuthor.value; });

        // Compare
        dom.pwCompareBtn.addEventListener('click', () => {
            state.compare = !state.compare;
            dom.pwCompareBtn.classList.toggle('is-active', state.compare);
            dom.pwCompareBtn.setAttribute('aria-pressed', state.compare);
            requestRender();
        });

        // Apply (export)
        dom.pwApplyBtn.addEventListener('click', exportAll);

        // Batch add — opens the original uploader's hidden input
        dom.pwBatchAdd.addEventListener('click', () => {
            const u = dom.pwUpload;
            if (u && u.__uploaderClick) { u.__uploaderClick(); return; }
            // Fallback: query the data-uploader-for input
            const inp = document.querySelector('[data-uploader-for="pwUpload"]')
                || document.querySelector('#pwUpload .tu-static-input');
            if (inp) inp.click();
        });

        // Cancel batch
        dom.pwCancelBtn.addEventListener('click', () => { exportCancelled = true; });

        // Presets
        dom.pwPresetSave.addEventListener('click', savePreset);
        renderPresets();

        // Templates
        document.querySelectorAll('.pw-tpl-chip').forEach((chip) => {
            chip.addEventListener('click', () => applyTemplate(chip.dataset.tpl));
        });

        // Auto-contrast 1-click fix
        const fixBtn = document.getElementById('pwContrastFix');
        if (fixBtn) fixBtn.addEventListener('click', applyContrastFix);
    }

    function updatePositionButtons() {
        document.querySelectorAll('.pw-pos-cell').forEach((c) => {
            c.classList.toggle('is-active', c.dataset.pos === state.layout.position);
        });
    }

    function bindRange(rangeEl, labelEl, applyFn) {
        if (!rangeEl) return;
        const update = () => {
            const v = parseFloat(rangeEl.value);
            const label = applyFn(v);
            if (labelEl) labelEl.textContent = label;
            requestRender();
        };
        rangeEl.addEventListener('input', update);
        // Initialize state from default value
        update();
    }

    function sanitizeHex(s) {
        s = String(s || '').trim();
        if (!s.startsWith('#')) s = '#' + s;
        if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
        if (/^#[0-9a-fA-F]{3}$/.test(s)) return s.toLowerCase();
        return null;
    }

    // ───── Quick-start templates ─────
    // Each template is a partial state snapshot — same shape as a preset, but
    // baked into the source instead of saved in localStorage. Applying one
    // pre-fills text/font/color/opacity/position so the first-time user sees
    // a finished result in two clicks instead of designing from scratch.
    const TEMPLATES = {
        minimal: {
            text: { value: '© Your Name', font: stackOf('sys-sans'), size: 0.028, color: '#ffffff', opacity: 0.55, rotation: 0, letterSpacing: 0, bold: false, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: true, color: '#000', blur: 6 } },
            layout: { position: 'br', margin: 0.025, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        studio: {
            text: { value: 'Your Studio', font: stackOf('playfair'), size: 0.06, color: '#ffffff', opacity: 0.72, rotation: 0, letterSpacing: 2, bold: false, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: true, color: '#000', blur: 10 } },
            layout: { position: 'bc', margin: 0.04, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        photo_pro: {
            text: { value: 'PREVIEW · Your Name', font: stackOf('oswald'), size: 0.03, color: '#ffffff', opacity: 0.32, rotation: 0, letterSpacing: 3, bold: true, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: false, color: '#000', blur: 4 } },
            layout: { position: 'mc', margin: 0.03, tile: { enabled: true, angle: -28, stepX: 0.42, stepY: 0.32 } },
        },
        corporate: {
            text: { value: '© 2026 Company', font: stackOf('montserrat'), size: 0.032, color: '#ffffff', opacity: 0.85, rotation: 0, letterSpacing: 1, bold: true, italic: false, stroke: { enabled: true, color: '#000000', width: 1.5 }, shadow: { enabled: false, color: '#000', blur: 4 } },
            layout: { position: 'tl', margin: 0.025, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        bold: {
            text: { value: 'BRAND', font: stackOf('russo'), size: 0.10, color: '#ffffff', opacity: 0.88, rotation: -12, letterSpacing: 4, bold: true, italic: false, stroke: { enabled: true, color: '#000000', width: 3 }, shadow: { enabled: true, color: '#000', blur: 4 } },
            layout: { position: 'bc', margin: 0.06, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        vintage: {
            text: { value: 'EST · YOUR NAME', font: stackOf('yeseva'), size: 0.045, color: '#f5e6c5', opacity: 0.78, rotation: 0, letterSpacing: 5, bold: false, italic: false, stroke: { enabled: true, color: '#3a2e1a', width: 1 }, shadow: { enabled: true, color: '#000', blur: 8 } },
            layout: { position: 'bc', margin: 0.05, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        food: {
            text: { value: 'yourblog.com', font: stackOf('pacifico'), size: 0.075, color: '#ffffff', opacity: 0.82, rotation: -6, letterSpacing: 0, bold: false, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: true, color: '#000', blur: 12 } },
            layout: { position: 'bc', margin: 0.04, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        url: {
            text: { value: 'yoursite.com', font: stackOf('sys-mono'), size: 0.025, color: '#ffffff', opacity: 0.65, rotation: 0, letterSpacing: 0, bold: false, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: true, color: '#000', blur: 4 } },
            layout: { position: 'br', margin: 0.02, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        copyright: {
            text: { value: '© 2026 · Your Name', font: stackOf('sys-sans'), size: 0.028, color: '#ffffff', opacity: 0.6, rotation: 0, letterSpacing: 1, bold: false, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: true, color: '#000', blur: 6 } },
            layout: { position: 'br', margin: 0.025, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
        handwritten: {
            text: { value: 'Your Name', font: stackOf('caveat'), size: 0.06, color: '#ffffff', opacity: 0.78, rotation: -4, letterSpacing: 0, bold: false, italic: false, stroke: { enabled: false, color: '#000', width: 2 }, shadow: { enabled: true, color: '#000', blur: 10 } },
            layout: { position: 'br', margin: 0.04, tile: { enabled: false, angle: -30, stepX: 0.3, stepY: 0.3 } },
        },
    };

    function applyTemplate(name) {
        const tpl = TEMPLATES[name];
        if (!tpl) return;
        // Merge template into state (text + layout). Keep logo + output settings
        // — user might have already uploaded a logo or set EXIF prefs.
        Object.assign(state.text, JSON.parse(JSON.stringify(tpl.text)));
        Object.assign(state.layout, JSON.parse(JSON.stringify(tpl.layout)));
        // Make sure nested structures (stroke/shadow/tile) are fully overwritten, not partial-merged.
        state.text.stroke = Object.assign({}, tpl.text.stroke);
        state.text.shadow = Object.assign({}, tpl.text.shadow);
        state.layout.tile = Object.assign({}, tpl.layout.tile);
        ae('template_applied', { name });
        syncControlsFromState();
        // Visually mark which chip is active so user sees what they picked.
        document.querySelectorAll('.pw-tpl-chip').forEach((c) => c.classList.toggle('is-applied', c.dataset.tpl === name));
        requestRender();
    }

    // ───── Auto-contrast 1-click fix ─────
    function applyContrastFix() {
        const hintEl = dom.pwContrastHint;
        const dir = hintEl && hintEl.dataset.direction;
        // 'dark' direction → background is dark, suggest LIGHT text (white).
        // 'bright' direction → background is bright, suggest DARK text (black).
        const newColor = dir === 'dark' ? '#ffffff' : (dir === 'bright' ? '#000000' : null);
        if (!newColor) return;
        state.text.color = newColor;
        dom.pwColor.value = newColor;
        dom.pwColorHex.value = newColor;
        ae('auto_contrast_applied', { direction: dir, to: newColor });
        requestRender();
    }

    // ───── Presets ─────
    const PRESET_LS_KEY = 'pw:presets:v1';

    function loadPresets() {
        try {
            const raw = localStorage.getItem(PRESET_LS_KEY);
            if (!raw) return [];
            return JSON.parse(raw);
        } catch (e) { return []; }
    }

    function persistPresets(list) {
        try { localStorage.setItem(PRESET_LS_KEY, JSON.stringify(list)); } catch (e) { }
    }

    function snapshotForPreset() {
        return {
            text: JSON.parse(JSON.stringify(state.text)),
            logo: { width: state.logo.width, opacity: state.logo.opacity, rotation: state.logo.rotation },
            layout: JSON.parse(JSON.stringify(state.layout)),
            output: {
                format: state.output.format, size: state.output.size, quality: state.output.quality,
                preserveExif: state.output.preserveExif, stripGps: state.output.stripGps,
                stripAll: state.output.stripAll,
            },
        };
    }

    function applyPreset(p) {
        if (!p) return;
        if (p.text) Object.assign(state.text, p.text);
        if (p.logo) Object.assign(state.logo, p.logo);
        if (p.layout) Object.assign(state.layout, p.layout);
        if (p.output) Object.assign(state.output, p.output);
        syncControlsFromState();
        requestRender();
    }

    function savePreset() {
        const name = (prompt(T.preset_save_name || 'Preset name?') || '').trim();
        if (!name) return;
        const list = loadPresets().filter((p) => p.name !== name);
        list.push({ name, data: snapshotForPreset() });
        persistPresets(list);
        renderPresets();
        toast(T.preset_saved || 'Preset saved', 'success');
        ae('preset_saved', { name });
    }

    function renderPresets() {
        const list = loadPresets();
        const el = dom.pwPresetList;
        el.innerHTML = '';
        list.forEach((p) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'pw-preset-chip';
            chip.innerHTML = `<span>${escapeHtml(p.name)}</span>`;
            chip.addEventListener('click', () => { applyPreset(p.data); ae('preset_loaded', { name: p.name }); });
            const del = document.createElement('span');
            del.className = 'pw-preset-chip-del';
            del.title = T.preset_delete || 'Delete';
            del.setAttribute('role', 'button');
            del.setAttribute('aria-label', T.preset_delete || 'Delete');
            del.textContent = '×';
            del.addEventListener('click', (e) => {
                e.stopPropagation();
                const next = loadPresets().filter((x) => x.name !== p.name);
                persistPresets(next);
                renderPresets();
            });
            chip.appendChild(del);
            el.appendChild(chip);
        });
    }

    function escapeHtml(s) {
        return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    }

    function syncControlsFromState() {
        dom.pwText.value = state.text.value;
        dom.pwFont.value = state.text.font;
        updateFontCurrent();
        dom.pwSize.value = Math.round(state.text.size * 100);
        dom.pwSize.dispatchEvent(new Event('input'));
        dom.pwOpacity.value = Math.round(state.text.opacity * 100);
        dom.pwOpacity.dispatchEvent(new Event('input'));
        dom.pwColor.value = state.text.color;
        dom.pwColorHex.value = state.text.color;
        dom.pwRotation.value = state.text.rotation;
        dom.pwRotation.dispatchEvent(new Event('input'));
        dom.pwLetterSpacing.value = state.text.letterSpacing;
        dom.pwLetterSpacing.dispatchEvent(new Event('input'));
        dom.pwBold.classList.toggle('is-active', !!state.text.bold);
        dom.pwItalic.classList.toggle('is-active', !!state.text.italic);
        dom.pwStrokeEnabled.checked = !!state.text.stroke.enabled;
        dom.pwStrokeRow.hidden = !state.text.stroke.enabled;
        dom.pwStrokeColor.value = state.text.stroke.color;
        dom.pwStrokeWidth.value = state.text.stroke.width;
        dom.pwStrokeWidth.dispatchEvent(new Event('input'));
        dom.pwShadowEnabled.checked = !!state.text.shadow.enabled;
        dom.pwShadowRow.hidden = !state.text.shadow.enabled;
        dom.pwShadowColor.value = state.text.shadow.color;
        dom.pwShadowBlur.value = state.text.shadow.blur;
        dom.pwShadowBlur.dispatchEvent(new Event('input'));
        dom.pwLogoWidth.value = Math.round(state.logo.width * 100);
        dom.pwLogoWidth.dispatchEvent(new Event('input'));
        dom.pwLogoOpacity.value = Math.round(state.logo.opacity * 100);
        dom.pwLogoOpacity.dispatchEvent(new Event('input'));
        dom.pwLogoRotation.value = state.logo.rotation;
        dom.pwLogoRotation.dispatchEvent(new Event('input'));
        dom.pwMargin.value = Math.round(state.layout.margin * 100);
        dom.pwMargin.dispatchEvent(new Event('input'));
        updatePositionButtons();
        dom.pwTile.checked = !!state.layout.tile.enabled;
        dom.pwTileRow.hidden = !state.layout.tile.enabled;
        dom.pwTileAngle.value = state.layout.tile.angle;
        dom.pwTileAngle.dispatchEvent(new Event('input'));
        dom.pwTileStepX.value = Math.round(state.layout.tile.stepX * 100);
        dom.pwTileStepX.dispatchEvent(new Event('input'));
        dom.pwTileStepY.value = Math.round(state.layout.tile.stepY * 100);
        dom.pwTileStepY.dispatchEvent(new Event('input'));
        dom.pwFormat.value = state.output.format;
        if (dom.pwExportSize) {
            const want = String(state.output.size || 'original');
            dom.pwExportSize.value = want;
            // A value with no matching option leaves the select empty — reset.
            if (dom.pwExportSize.value !== want) { dom.pwExportSize.value = 'original'; state.output.size = 'original'; }
            // Re-gate after a programmatic set: a preset saved on Premium can
            // land on a now-locked option — gate() moves it down VISIBLY
            // (persistent note + change event), never silently.
            if (window.__qualityTier) window.__qualityTier.gate(dom.pwExportSize, 'side');
        }
        dom.pwQuality.value = Math.round(state.output.quality * 100);
        dom.pwQuality.dispatchEvent(new Event('input'));
        dom.pwPreserveExif.checked = !!state.output.preserveExif;
        dom.pwStripGps.checked = !!state.output.stripGps;
        dom.pwStripAll.checked = !!state.output.stripAll;
    }

    // ───── Export pipeline ─────
    let exportCancelled = false;

    // Long-side plan ceiling for the delivered image (image_max_side). Premium
    // is uncapped; a missing/broken tier snapshot fails OPEN — same invariant
    // as quality-tier.js itself.
    function planSideCeiling() {
        const qt = window.__qualityTier;
        if (!qt || (qt.isPro && qt.isPro())) return Infinity;
        return qt.cap ? qt.cap('side') : Infinity;
    }

    // Delivered dims = the user's Export-size choice ∩ the plan ceiling, aspect
    // kept, never upscaled. The ceiling is DISCLOSED before commit — the locked
    // options carry a «Premium» badge and "Original" an «up to N px» suffix
    // (quality-tier.js gate), and the size pill shows «→ W × H» — so this clamp
    // is never a silent downgrade (feedback_no_silent_quality_downgrade).
    function exportDimsFor(entry) {
        const w = entry.naturalWidth, h = entry.naturalHeight;
        const long0 = Math.max(w, h);
        const chosen = state.output.size === 'original' ? Infinity : (parseInt(state.output.size, 10) || Infinity);
        const wanted = Math.min(long0, chosen);
        const side = Math.min(wanted, planSideCeiling());
        if (side >= long0) return { w, h, capped: false };
        const k = side / long0;
        return {
            w: Math.max(1, Math.round(w * k)),
            h: Math.max(1, Math.round(h * k)),
            // capped = the PLAN (not the user's own pick) was the binding
            // limit — the upsell-conversion signal for telemetry.
            capped: side < wanted,
        };
    }

    async function exportAll() {
        if (!state.files.length) { toast(T.err_no_files || 'Select at least one photo.', 'error'); return; }
        if (!state.text.value && !state.logo.image) { toast(T.err_empty_text || 'Add some watermark text first.', 'error'); return; }

        exportCancelled = false;
        dom.pwApplyBtn.disabled = true;
        dom.pwCancelBtn.hidden = state.files.length < 2;
        dom.pwApplyLabel.textContent = T.applying || 'Applying…';
        showProgress(0, '');

        const exifMod = state.output.preserveExif && !state.output.stripAll
            ? await loadModule('exif')
            : null;
        const c2paReady = state.output.c2pa.enabled && window.C2PA
            ? await window.C2PA.ensure().catch(() => ({ ready: false, reason: 'ensure_threw' }))
            : null;
        if (state.output.c2pa.enabled && (!c2paReady || !c2paReady.ready)) {
            // Surface the friendly toast once before the batch loop so the user
            // isn't surprised when the downloads land without a manifest.
            toast(T.c2pa_failed || 'Content Credentials unavailable — downloading without', 'warning');
        }

        // Fetch the chosen web font before the export loop rasterises it, else the
        // saved PNG/JPG bakes in a fallback typeface (canvas has no swap fallback).
        if (state.text.value) await ensureFontLoaded(state.text.font);

        const startedAt = performance.now();
        const outputs = [];

        // Full cut so we can answer: how many use tile vs corner? text vs logo?
        // c2pa attempt rate? strip-gps rate? font popularity at export time?
        const textFamily = cleanFamilyName(firstFamily(state.text.font)).slice(0, 40);
        ae('export_started', {
            count: state.files.length,
            format: state.output.format,
            size: String(state.output.size),
            c2pa: state.output.c2pa.enabled ? 1 : 0,
            preserve_exif: state.output.preserveExif ? 1 : 0,
            strip_gps: state.output.stripGps ? 1 : 0,
            strip_all: state.output.stripAll ? 1 : 0,
            tile: state.layout.tile.enabled ? 1 : 0,
            position: state.layout.tile.enabled ? 'tile' : state.layout.position,
            has_text: state.text.value ? 1 : 0,
            has_logo: state.logo.image ? 1 : 0,
            font: state.text.value ? textFamily : '',
            stroke: state.text.stroke.enabled ? 1 : 0,
            shadow: state.text.shadow.enabled ? 1 : 0,
            text_rotation: state.text.rotation,
            quality: Math.round(state.output.quality * 100),
        });

        for (let i = 0; i < state.files.length; i++) {
            if (exportCancelled) break;
            showProgress(Math.round((i / state.files.length) * 100), (T.processing || 'Processing {current} of {total}').replace('{current}', i + 1).replace('{total}', state.files.length));
            try {
                const out = await exportOne(state.files[i], exifMod, c2paReady && c2paReady.ready ? window.C2PA : null);
                if (out) outputs.push(out);
            } catch (e) {
                console.warn('[pw] export failed', state.files[i].file.name, e);
                aeCrit('export_failed', { reason: String(e && e.message || e).slice(0, 140), name: state.files[i].file.name });
                toast((T.err_export || 'Export failed for {name}.').replace('{name}', state.files[i].file.name), 'error');
            }
        }

        showProgress(100, (T.processed || 'Done — {count} photo(s) ready').replace('{count}', outputs.length));
        dom.pwApplyBtn.disabled = false;
        dom.pwApplyLabel.textContent = T.apply || 'Apply watermark';
        dom.pwCancelBtn.hidden = true;

        ae('export_complete', {
            count: outputs.length,
            planned: state.files.length,
            total_ms: Math.round(performance.now() - startedAt),
            // How many delivered files the PLAN ceiling shrank (user's own
            // size pick doesn't count) — the axis Premium sells on here.
            size_capped: outputs.filter((o) => o.capped).length,
        });

        if (outputs.length === 1) {
            triggerDownload(outputs[0].blob, outputs[0].filename);
            announce(T.processed ? T.processed.replace('{count}', 1) : '');
            setTimeout(() => hideProgress(), 1500);
        } else if (outputs.length > 1) {
            // Пачка — ТОЛЬКО через общий примитив: папка → один архив → честный
            // отказ ДО работы с числами. Свой JSZip здесь собирал архив поверх
            // живых результатов и падал «Array buffer allocation failed» без
            // единого слова человеку (TIMBRICA-FRONTEND-91, 07.09.2026); запасной
            // «по файлу за раз» тратил ОДИН жест на N загрузок и травил вкладку.
            let deliver = window.__batchDeliver || null;
            if (!deliver && window.__ensureBatchDeliver) {
                try { deliver = await window.__ensureBatchDeliver(); } catch (_) { deliver = null; }
            }
            if (deliver && deliver.deliver) {
                const res = await deliver.deliver(
                    outputs.map((o) => ({ name: o.filename, blob: o.blob })),
                    { slug: 'photo-watermark', zipName: 'photo-watermark.zip' }
                );
                ae('batch_delivered', { via: res && res.via, n: outputs.length });
                if (res && res.via !== 'refused') announce(T.processed ? T.processed.replace('{count}', outputs.length) : '');
                setTimeout(() => hideProgress(), 1500);
            } else {
                hideProgress();
                toast(T.err_export_generic || 'Export failed.', 'error');
            }
        } else {
            hideProgress();
            if (!exportCancelled) toast(T.err_export_generic || 'Export failed.', 'error');
        }
    }

    async function exportOne(fileEntry, exifMod, c2paApi) {
        const { file, image, exifBytes } = fileEntry;
        const dims = exportDimsFor(fileEntry);
        const cv = document.createElement('canvas');
        cv.width = dims.w;
        cv.height = dims.h;
        const ctx = cv.getContext('2d');
        // One-shot downscale: ask for the best filter the engine has (no-op
        // where unsupported) so a 4032→2560 resize stays crisp.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        drawScene(ctx, image, dims.w, dims.h);

        // Determine output mime + extension
        const requested = state.output.format;
        let mime, ext;
        if (requested === 'original') {
            mime = file.type || 'image/jpeg';
            if (mime === 'image/heic' || mime === 'image/heif' || /\.(heic|heif)$/i.test(file.name)) {
                // Browsers can't encode HEIC; downgrade to JPEG silently.
                mime = 'image/jpeg';
            }
            ext = mime.split('/')[1] === 'jpeg' ? 'jpg' : mime.split('/')[1];
        } else {
            mime = ({ jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' })[requested];
            ext = requested === 'jpeg' ? 'jpg' : requested;
        }

        const quality = (mime === 'image/jpeg' || mime === 'image/webp') ? state.output.quality : undefined;
        let blob = await canvasToBlob(cv, mime, quality);
        if (!blob) throw new Error('canvas_toblob_failed');

        // EXIF: re-inject for JPEG when preserving
        if (mime === 'image/jpeg' && state.output.preserveExif && !state.output.stripAll && exifBytes && exifMod) {
            try {
                blob = await exifMod.injectInto(blob, exifBytes, { stripGps: state.output.stripGps });
            } catch (e) {
                console.warn('[pw] EXIF inject failed', e);
            }
        }

        // C2PA: sign output (strictly additive — signer always returns a Blob,
        // unsigned when anything went wrong, so we never break the download).
        if (state.output.c2pa.enabled && c2paApi && c2paApi.sign) {
            try {
                const signed = await c2paApi.sign(blob, {
                    title: file.name,
                    author: state.output.c2pa.author || '',
                    mime,
                });
                if (signed && signed !== blob) {
                    blob = signed;
                    ae('c2pa_attached', { mime });
                }
            } catch (e) {
                // Telemetry only — signer already returned the original blob.
                aeCrit('c2pa_failed', { reason: String(e && e.message || e).slice(0, 140) });
            }
        }

        const baseName = file.name.replace(/\.[^.]+$/, '');
        const outName = `${baseName}-watermark.${ext}`;
        return { blob, filename: outName, mime, capped: dims.capped };
    }

    function canvasToBlob(cv, mime, quality) {
        return new Promise((resolve) => {
            try {
                cv.toBlob((b) => resolve(b), mime, quality);
            } catch (e) {
                resolve(null);
            }
        });
    }

    function triggerDownload(blob, name) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    }

    function showProgress(pct, text) {
        dom.pwProgress.classList.add('is-visible');
        dom.pwProgressFill.style.width = pct + '%';
        dom.pwProgressText.textContent = text || '';
    }
    function hideProgress() { dom.pwProgress.classList.remove('is-visible'); }

    // ───── Module loader (lazy imports) ─────
    // app.js is served via @light() which routes through assets.timbrica.com
    // on non-RU locales. From a cross-origin classic script, dynamic import()
    // with a path-only specifier resolves against `about:blank` and fails. Use
    // an absolute URL anchored to the SAME origin as the running script — that
    // way the import is same-origin from the script's perspective and bypasses
    // CORS entirely (CORS lives on the edge, not the origin server).
    const moduleCache = {};
    const MODULE_BASE = (function () {
        try {
            const src = document.currentScript && document.currentScript.src;
            if (src) return new URL('./', src).href;
        } catch (e) { /* fallthrough */ }
        return `${location.origin}/js/photo-watermark/`;
    })();

    function loadModule(name) {
        if (moduleCache[name]) return moduleCache[name];
        const path = `${MODULE_BASE}${name}.js`;
        moduleCache[name] = import(path).then((m) => m.default || m).catch((e) => {
            console.warn('[pw] module load failed', name, e);
            return null;
        });
        return moduleCache[name];
    }

    function preloadC2pa() {
        if (window.C2PA && window.C2PA.ensure) window.C2PA.ensure();
    }

    // ───── Init ─────
    function init() {
        refs();
        bindControls();
        buildFontPicker();
        bindDrag();
        // Listen for uploader file events
        dom.pwUpload.addEventListener('tool-uploader:file', (e) => {
            const d = e.detail || {};
            const files = d.files || (d.file ? [d.file] : []);
            addFiles(files);
        });
        // Keep layout responsive on resize
        let resizeT = null;
        window.addEventListener('resize', () => {
            clearTimeout(resizeT);
            resizeT = setTimeout(() => requestRender(), 120);
        });
        // The tier snapshot lands asynchronously; the size pill's «→ W × H»
        // disclosure depends on it (quality-tier.js re-gates the select itself).
        document.addEventListener('tier-bootstrap:ready', () => { if (state.files.length) requestRender(); });
    }

    // Expose state immediately so tests don't race the init() bind step,
    // and so a runtime error in bindControls doesn't strand window.__PW undefined.
    window.__PW = { state, render: requestRender, addFiles, exportAll };

    function safeInit() {
        try { init(); }
        catch (e) {
            console.error('[pw] init failed:', e && (e.stack || e.message || e));
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', safeInit);
    } else {
        safeInit();
    }
})();
